import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import contactService from '../services/contactService';

const ContactSettingsPage = () => {
  const [contactInfo, setContactInfo] = useState({
    address: '',
    addressLink: '',
    phone: '',
    email: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Load contact info on component mount
  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const data = await contactService.getContactInfo();
        
        // Handle both old and new address formats
        let address = '';
        let addressLink = '';
        
        if (typeof data.address === 'string') {
          // New format
          address = data.address;
          addressLink = data.addressLink || '';
        } else if (data.address && typeof data.address === 'object') {
          // Old format - convert to string
          const street = data.address.street || '';
          const city = data.address.city || '';
          address = street && city ? `${street}, ${city}` : street || city;
          addressLink = data.addressLink || '';
        }
        
        setContactInfo({
          address,
          addressLink,
          phone: data.phone || '',
          email: data.email || ''
        });
      } catch (error) {
        console.error('Failed to load contact info:', error);
        setMessage({ type: 'danger', text: 'Failed to load contact information' });
      } finally {
        setLoading(false);
      }
    };
    
    loadContactInfo();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      await contactService.updateContactInfo(contactInfo);
      setMessage({ type: 'success', text: 'Contact information updated successfully!' });
    } catch (error) {
      console.error('Failed to update contact info:', error);
      setMessage({ type: 'danger', text: 'Failed to update contact information' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <h2 className="mb-4">Contact Information Settings</h2>
      
      {message.text && (
        <Alert variant={message.type} dismissible onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}
      
      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <h5 className="mb-3">Address</h5>
            <Form.Group className="mb-3">
              <Form.Label>Full Address</Form.Label>
              <Form.Control
                type="text"
                value={contactInfo.address}
                onChange={(e) => setContactInfo({
                  ...contactInfo,
                  address: e.target.value
                })}
                placeholder="Enter full address (e.g., 123 Main St, City, Country)"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Google Maps Link</Form.Label>
              <Form.Control
                type="url"
                value={contactInfo.addressLink}
                onChange={(e) => setContactInfo({
                  ...contactInfo,
                  addressLink: e.target.value
                })}
                placeholder="https://maps.google.com/..."
              />
              <Form.Text className="text-muted">
                Paste the Google Maps link for this address
              </Form.Text>
            </Form.Group>

            <h5 className="mb-3">Contact Details</h5>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo({
                  ...contactInfo,
                  phone: e.target.value
                })}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                value={contactInfo.email}
                onChange={(e) => setContactInfo({
                  ...contactInfo,
                  email: e.target.value
                })}
              />
            </Form.Group>



            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ContactSettingsPage;